<template>
  <ListViewBanner :add-route="{ name: 'app_province_new' }">
    Provinces
  </ListViewBanner>

  <ProvinceSearch @search="searchParams = $event" />

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th class="w-20">
          Logo
        </th>

        <th>Name</th>
        <th> Districts</th>

        <th>
          <span class="sr-only">Actions</span>
        </th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!provinces.length"
        :colspan="4"
      >
        <template v-if="provinces.length">
          <tr
            v-for="province in provinces"
            :key="province.id"
            class="overflow-visible"
          >
            <td class="flex items-center gap-2">
              <img
                class="block h-10 w-auto"
                :alt="province.name"
                :src="province.logo"
              >
            </td>

            <td>
              {{ province.name }}

              <template v-if="province.archived">
                <BadgeArchived />
              </template>
            </td>

            <td>
              <div class="flex flex-col">
                <span>
                  {{ province.districts[0]?.name }}
                </span>

                <template v-if="province.districts?.length > 1">
                  <UsersTooltip
                    :user="province"
                    :districts="province.districts"
                    class="users-list-tooltip"
                  />
                </template>
              </div>
            </td>

            <td>
              <template v-if="province.archived">
                <RestoreButton @click="entityToRestore = province" />
              </template>

              <template v-else>
                <AppDropdown>
                  <AppDropdownItem
                    text="Edit"
                    :to="{ name: 'app_province_edit', params: { province: province.id } }"
                  />

                  <AppDropdownItem
                    text="Archive"
                    @click="entityToArchive = province"
                  />
                </AppDropdown>
              </template>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />
  </AppContainer>

  <!-- Archive Modal -->
  <template v-if="entityToArchive">
    <ConfirmationModal
      title="Archive Province"
      :processing="archiving"
      @confirm="archiveEntity"
      @close="entityToArchive = null"
    >
      <p class="mb-0.5">
        <span>Name: {{ entityToArchive?.name }}</span>
      </p>

      <p>
        Are you sure you want to <strong>ARCHIVE</strong> this Province?
      </p>
    </ConfirmationModal>
  </template>

  <!-- Restore Modal -->
  <template v-if="entityToRestore">
    <ConfirmationModal
      title="Restore Province"
      :processing="restoring"
      @confirm="restoreEntity"
      @close="entityToRestore = null"
    >
      <p class="mb-0.5">
        <span>Name: {{ entityToRestore?.name }}</span>
      </p>

      <p>
        Are you sure you want to <strong>RESTORE</strong> this Province?
      </p>
    </ConfirmationModal>
  </template>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';

import { useHttp, useEntityArchiver, useEntityRestorer } from '@/composables';

import { AppContainer, AppPagination } from '@/components/app';
import { BadgeArchived } from '@/components/badges';
import { ListViewBanner } from '@/components/banners';
import { RestoreButton } from '@/components/buttons';
import { AppDropdown, AppDropdownItem } from '@/components/dropdowns';
import { ProvinceSearch } from '@/components/forms/search';
import { ConfirmationModal } from '@/components/modals';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { UsersTooltip } from '@/components/tooltips';

const loading = ref();
const provinces = ref([]);
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});
const searchParams = ref({
  page: 1,
});

const loadProvinces = async () => {
  loading.value = true;
  provinces.value = [];

  const response = await useHttp().get('/provinces', searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    provinces.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const { archiving, entityToArchive, archiveEntity } = useEntityArchiver('provinces', loadProvinces);
const { restoring, entityToRestore, restoreEntity } = useEntityRestorer('provinces', loadProvinces);

watchEffect(loadProvinces);
</script>
